import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useMaintenanceDocumentsQuery } from '../adminTools.queries';
import {
  useUploadMaintenanceDocumentMutation,
  useDeleteMaintenanceDocumentMutation,
  useProcessMaintenanceDocumentMutation,
} from '../adminTools.mutations';
import type {
  MaintenanceDocumentListResponse,
  MaintenanceDocumentUploadResponse,
} from '../types/adminTools.types';

export interface UseMaintenanceDocumentsReturn {
  // Dialog state
  dialogOpen: boolean;
  loading: boolean;
  docsData: MaintenanceDocumentListResponse | null;
  error: string;

  // Upload form state
  selectedFile: File | null;
  documentTitle: string;
  documentType: string;
  documentVersion: string;
  droneModelFilter: string;
  manufacturerFilter: string;
  uploading: boolean;
  uploadResult: MaintenanceDocumentUploadResponse | null;
  uploadError: string;

  // Document actions state
  deletingDocKey: string | null;
  processingDocKey: string | null;

  // Actions
  openDialog: () => Promise<void>;
  closeDialog: () => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  uploadDocument: () => Promise<void>;
  deleteDocument: (s3Key: string) => Promise<void>;
  processDocument: (s3Key: string) => Promise<void>;

  // Form setters
  setDocumentTitle: (value: string) => void;
  setDocumentType: (value: string) => void;
  setDocumentVersion: (value: string) => void;
  setDroneModelFilter: (value: string) => void;
  setManufacturerFilter: (value: string) => void;
}

export function useMaintenanceDocuments(): UseMaintenanceDocumentsReturn {
  const { showError, showSuccess } = useErrorHandler();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Server data — fetched only while the dialog is open.
  const docsQuery = useMaintenanceDocumentsQuery(dialogOpen);
  const docsData = docsQuery.data ?? null;
  const loading = dialogOpen && docsQuery.isLoading;
  const error = docsQuery.isError ? 'Failed to fetch maintenance documents' : '';

  const uploadMutation = useUploadMaintenanceDocumentMutation();
  const deleteMutation = useDeleteMaintenanceDocumentMutation();
  const processMutation = useProcessMaintenanceDocumentMutation();

  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('manual');
  const [documentVersion, setDocumentVersion] = useState('');
  const [droneModelFilter, setDroneModelFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [uploadResult, setUploadResult] = useState<MaintenanceDocumentUploadResponse | null>(null);
  const [uploadError, setUploadError] = useState('');

  // Document actions state
  const [deletingDocKey, setDeletingDocKey] = useState<string | null>(null);
  const [processingDocKey, setProcessingDocKey] = useState<string | null>(null);

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setUploadResult(null);
    setUploadError('');
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setUploadResult(null);
    setUploadError('');
    setSelectedFile(null);
    setDocumentTitle('');
    setDocumentType('manual');
    setDocumentVersion('');
    setDroneModelFilter('');
    setManufacturerFilter('');
  }, []);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate PDF
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          setUploadError('Only PDF files are allowed');
          return;
        }
        // Validate size (max 50MB)
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
          setUploadError('File too large. Maximum size is 50MB');
          return;
        }
        setSelectedFile(file);
        setUploadError('');

        // Auto-populate title from filename if empty
        if (!documentTitle) {
          const filename = file.name.replace('.pdf', '');
          setDocumentTitle(filename);
        }
      }
    },
    [documentTitle]
  );

  const uploadDocument = useCallback(async () => {
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }
    if (!documentTitle.trim()) {
      setUploadError('Document title is required');
      return;
    }

    setUploadResult(null);
    setUploadError('');

    try {
      const result = await uploadMutation.mutateAsync({
        file: selectedFile,
        documentTitle: documentTitle.trim(),
        documentType,
        documentVersion: documentVersion.trim() || undefined,
        droneModel: droneModelFilter.trim() || undefined,
        manufacturer: manufacturerFilter.trim() || undefined,
      });

      setUploadResult(result);

      if (result.success) {
        // The mutation invalidates the documents list, refreshing it.
        // Reset upload form
        setSelectedFile(null);
        setDocumentTitle('');
        setDocumentType('manual');
        setDocumentVersion('');
        setDroneModelFilter('');
        setManufacturerFilter('');
        showSuccess('Document uploaded successfully');
      }
    } catch (err) {
      showError(err, 'Failed to upload document');
    }
  }, [
    selectedFile,
    documentTitle,
    documentType,
    documentVersion,
    droneModelFilter,
    manufacturerFilter,
    uploadMutation,
    showError,
    showSuccess,
  ]);

  const deleteDocument = useCallback(
    async (s3Key: string) => {
      if (!confirm(`Are you sure you want to delete this document?\n\nKey: ${s3Key}`)) {
        return;
      }

      setDeletingDocKey(s3Key);

      try {
        const result = await deleteMutation.mutateAsync(s3Key);

        if (!result.success) {
          // The mutation invalidates the documents list on success.
          showError(
            new Error(result.error || 'Failed to delete document'),
            'Delete document failed'
          );
        }
      } catch (err) {
        showError(err, 'Failed to delete document');
      } finally {
        setDeletingDocKey(null);
      }
    },
    [deleteMutation, showError]
  );

  const processDocument = useCallback(
    async (s3Key: string) => {
      setProcessingDocKey(s3Key);

      try {
        const result = await processMutation.mutateAsync(s3Key);

        if (result.success) {
          // The mutation invalidates the documents list to show updated status.
          // Show success message
          setUploadResult({
            success: true,
            message: result.message || `Processed into ${result.total_chunks} chunks`,
          });

          // Clear success message after 5 seconds
          setTimeout(() => setUploadResult(null), 5000);
        } else {
          showError(
            new Error(result.error || 'Failed to process document'),
            'Process document failed'
          );
        }
      } catch (err) {
        showError(err, 'Failed to process document');
      } finally {
        setProcessingDocKey(null);
      }
    },
    [processMutation, showError]
  );

  return {
    dialogOpen,
    loading,
    docsData,
    error,
    selectedFile,
    documentTitle,
    documentType,
    documentVersion,
    droneModelFilter,
    manufacturerFilter,
    uploading: uploadMutation.isPending,
    uploadResult,
    uploadError,
    deletingDocKey,
    processingDocKey,
    openDialog,
    closeDialog,
    handleFileSelect,
    uploadDocument,
    deleteDocument,
    processDocument,
    setDocumentTitle,
    setDocumentType,
    setDocumentVersion,
    setDroneModelFilter,
    setManufacturerFilter,
  };
}
